import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import queryString from "query-string";
import type { OAuthCallbackResponse, TokenRefreshResponse } from "../types/auth";
import { tokenManager } from "../lib/tokenManager";

// トークン期限切れの何秒前にリフレッシュを試みるか
const REFRESH_BUFFER_SECONDS = 5 * 60; // 5分前

// CSRF対策用のstateパラメータを生成
const OAUTH_STATE_KEY = "oauthState";
// PKCE 用の code_verifier を sessionStorage に保存するキー
const PKCE_VERIFIER_KEY = "pkceCodeVerifier";

function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("");
}

function generateOAuthState(): string {
  return generateRandomString(32);
}

// PKCE: 43〜128文字のランダム文字列を生成
function generateCodeVerifier(): string {
  return generateRandomString(32); // 64文字の hex 文字列
}

// PKCE: code_verifier から code_challenge（SHA256 → Base64URL）を計算
async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  // Base64URL エンコード（+ → -, / → _, = を除去）
  const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

interface UseZendeskAuthReturn {
  accessToken: string;
  isLoggingIn: boolean;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => void;
}

interface ZendeskAuthConfig {
  clientId: string;
  subdomain: string;
  redirectUri: string;
  proxyUrl: string;
}

export const useZendeskAuth = (config: ZendeskAuthConfig): UseZendeskAuthReturn => {
  // ローカルストレージから初期値を取得
  const [accessToken, setAccessToken] = useState<string>(() => {
    return localStorage.getItem("accessToken") || "";
  });
  const [tokenExpiry, setTokenExpiry] = useState<number>(() => {
    const expiry = localStorage.getItem("tokenExpiry");
    return expiry ? parseInt(expiry) : 0;
  });
  const [userId, setUserId] = useState<string>(() => {
    return localStorage.getItem("userId") || "";
  });
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);

  // 認証処理の重複実行を防ぐためのRef
  const loginInProgressRef = useRef<boolean>(false);
  const refreshInProgressRef = useRef<boolean>(false);

  // トークンの有効性チェック
  const isTokenValid = useCallback((): boolean => {
    if (!accessToken) return false;
    // expires_inが返されなかった場合（tokenExpiry === 0）、アクセストークンは無期限
    if (tokenExpiry === 0) return true;

    return Date.now() < tokenExpiry;
  }, [accessToken, tokenExpiry]);

  // 認証状態をクリア
  const clearAuthenticationState = useCallback(() => {
    setAccessToken("");
    setTokenExpiry(0);
    setUserId("");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("tokenExpiry");
    localStorage.removeItem("userId");
  }, []);

  // Zendesk OAuthページへリダイレクト（PKCE + state 付き）
  const redirectToZendeskOAuth = useCallback(async () => {
    console.log("useZendeskAuth: Redirecting to Zendesk OAuth");
    const state = generateOAuthState();
    sessionStorage.setItem(OAUTH_STATE_KEY, state);

    // PKCE: code_verifier を生成して保存、code_challenge を計算
    const codeVerifier = generateCodeVerifier();
    sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    const queryParams = queryString.stringify({
      response_type: "code",
      redirect_uri: config.redirectUri,
      client_id: config.clientId,
      scope: "read hc:write",
      state,
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
    });
    window.location.href = `https://${config.subdomain}.zendesk.com/oauth/authorizations/new?${queryParams}`;
  }, [config]);

  // OAuth認証処理
  const login = useCallback(async (): Promise<void> => {
    if (isLoggingIn || loginInProgressRef.current) return;

    loginInProgressRef.current = true;
    setIsLoggingIn(true);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");

      if (code) {
        // state パラメータの検証（CSRF対策）
        const returnedState = urlParams.get("state");
        const savedState = sessionStorage.getItem(OAUTH_STATE_KEY);
        sessionStorage.removeItem(OAUTH_STATE_KEY);

        if (!returnedState || !savedState || returnedState !== savedState) {
          console.error("useZendeskAuth: OAuth state mismatch — possible CSRF attack");
          throw new Error("OAuth state validation failed");
        }

        // PKCE: code_verifier を取り出す
        const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
        sessionStorage.removeItem(PKCE_VERIFIER_KEY);

        if (!codeVerifier) {
          console.error("useZendeskAuth: PKCE code_verifier not found");
          throw new Error("PKCE code_verifier missing");
        }

        // 認証コードをトークンに交換（code_verifier を含める）
        const response = await axios.post<OAuthCallbackResponse>(
          `${config.proxyUrl}/zendesk/oauth/callback`,
          { code: code, code_verifier: codeVerifier }
        );

        const { access_token, expires_in, user_id } = response.data;
        const expiryTime = expires_in > 0 ? Date.now() + (expires_in * 1000) : 0;

        setAccessToken(access_token);
        setTokenExpiry(expiryTime);
        setUserId(user_id);

        localStorage.setItem("accessToken", access_token);
        localStorage.setItem("tokenExpiry", expiryTime.toString());
        localStorage.setItem("userId", user_id);

        // URLからcodeパラメータを削除
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      } else if (!isTokenValid()) {
        // トークンが無効または期限切れ → Zendeskの認証ページにリダイレクト
        redirectToZendeskOAuth();
      }
    } catch (error) {
      console.error("Login error:", error);
      clearAuthenticationState();
    } finally {
      setIsLoggingIn(false);
      loginInProgressRef.current = false;
    }
  }, [isLoggingIn, isTokenValid, config, clearAuthenticationState, redirectToZendeskOAuth]);

  // ログアウト処理
  const logout = useCallback(() => {
    clearAuthenticationState();
    setIsLoggingIn(false);
    loginInProgressRef.current = false;
  }, [clearAuthenticationState]);

  // TokenManager にトークン状態を同期
  useEffect(() => {
    tokenManager.sync(accessToken, tokenExpiry);
  }, [accessToken, tokenExpiry]);

  // 認証処理の初期化
  useEffect(() => {
    if (!isTokenValid() && !isLoggingIn && !loginInProgressRef.current) {
      console.log("useZendeskAuth: Starting login process");
      login().catch((error) => {
        console.error("Initial login error:", error);
        setIsLoggingIn(false);
        loginInProgressRef.current = false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // リフレッシュトークンでアクセストークンを更新
  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    if (!userId || refreshInProgressRef.current) return false;

    refreshInProgressRef.current = true;
    try {
      console.log("useZendeskAuth: Attempting token refresh");
      const response = await axios.post<TokenRefreshResponse>(
        `${config.proxyUrl}/zendesk/refresh-token`,
        { user_id: userId }
      );

      const { access_token, expires_in, refreshed } = response.data;
      if (refreshed && access_token) {
        const expiryTime = expires_in > 0 ? Date.now() + (expires_in * 1000) : 0;

        setAccessToken(access_token);
        setTokenExpiry(expiryTime);

        localStorage.setItem("accessToken", access_token);
        localStorage.setItem("tokenExpiry", expiryTime.toString());

        console.log("useZendeskAuth: Token refreshed successfully");
        return true;
      }
      // refreshed=false はトークンがまだ有効（更新不要）
      return !refreshed;
    } catch (error) {
      console.error("useZendeskAuth: Token refresh failed:", error);
      return false;
    } finally {
      refreshInProgressRef.current = false;
    }
  }, [userId, config.proxyUrl]);

  // 定期的なトークン有効期限チェック（期限切れ前にリフレッシュ、失敗時のみOAuth再認証）
  useEffect(() => {
    if (!accessToken || isLoggingIn) return;
    // 無期限トークンの場合はチェック不要
    if (tokenExpiry === 0) return;

    const interval = setInterval(async () => {
      if (loginInProgressRef.current || refreshInProgressRef.current) return;

      const remainingMs = tokenExpiry - Date.now();
      const remainingSeconds = Math.floor(remainingMs / 1000);

      if (remainingSeconds <= 0) {
        // 既に期限切れ → リフレッシュを試みる
        console.log("useZendeskAuth: Token expired, attempting refresh");
        const success = await refreshAccessToken();
        if (!success) {
          console.log("useZendeskAuth: Refresh failed, redirecting to OAuth");
          clearAuthenticationState();
          redirectToZendeskOAuth();
        }
      } else if (remainingSeconds <= REFRESH_BUFFER_SECONDS) {
        // 期限切れが近い → バックグラウンドでリフレッシュ
        console.log(`useZendeskAuth: Token expiring in ${remainingSeconds}s, refreshing proactively`);
        await refreshAccessToken();
      }
    }, 60 * 1000); // 1分間隔でチェック

    return () => clearInterval(interval);
  }, [accessToken, isLoggingIn, tokenExpiry, refreshAccessToken, clearAuthenticationState, redirectToZendeskOAuth]);

  return {
    accessToken,
    isLoggingIn,
    isAuthenticated: isTokenValid(),
    login,
    logout,
  };
};
